/**
 * Featured-collection carousel autoplay:
 * - continuous: smooth scroll + duplicated slides, seamless loop
 * - step: advance by "next" on an interval
 */
(function () {
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parseIntervalSeconds(component) {
    const raw = component.getAttribute('data-carousel-autoplay-interval');
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(60, n) * 1000;
    return 6000;
  }

  function parseSpeed(component) {
    const raw = component.getAttribute('data-carousel-scroll-speed');
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(10, Math.max(1, n));
    return 5;
  }

  function pixelsPerSecond(speed1to10) {
    return 6 + speed1to10 * 7;
  }

  function initContinuousAutoplay(component, slider, sectionEl) {
    let pauseCount = 0;
    let inViewport = true;
    let pageVisible = document.visibilityState !== 'hidden';
    let io = null;
    let rafId = null;
    let lastTs = 0;
    let ro = null;
    let touchPauseTimer = null;
    let arrowResumeTimer = null;
    const navAbort = new AbortController();

    function shouldRun() {
      return inViewport && pageVisible && pauseCount === 0;
    }

    function stopRaf() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastTs = 0;
    }

    function tick(ts) {
      if (!shouldRun()) {
        stopRaf();
        return;
      }
      if (!lastTs) lastTs = ts;
      const dt = Math.min(0.1, (ts - lastTs) / 1000);
      lastTs = ts;

      const half = slider.scrollWidth / 2;
      if (half < 10) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const pxPerSec = pixelsPerSecond(parseSpeed(component));
      slider.scrollLeft += pxPerSec * dt;

      if (slider.scrollLeft >= half - 1) {
        slider.scrollLeft -= half;
      }

      rafId = requestAnimationFrame(tick);
    }

    function startRaf() {
      stopRaf();
      if (!shouldRun()) return;
      lastTs = 0;
      rafId = requestAnimationFrame(tick);
    }

    function pause() {
      pauseCount++;
      stopRaf();
    }

    function resume() {
      pauseCount = Math.max(0, pauseCount - 1);
      if (shouldRun()) startRaf();
    }

    const prevBtn = component.querySelector('button[name="previous"]');
    const nextBtn = component.querySelector('button[name="next"]');

    function nudgeCarousel(dir) {
      pause();
      clearTimeout(arrowResumeTimer);
      const stepPx = Math.max(200, Math.round(slider.clientWidth * 0.85));
      slider.scrollBy({ left: stepPx * dir, behavior: 'smooth' });
      arrowResumeTimer = setTimeout(function () {
        resume();
      }, 450);
    }

    if (prevBtn) {
      prevBtn.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          nudgeCarousel(-1);
        },
        { capture: true, signal: navAbort.signal }
      );
    }
    if (nextBtn) {
      nextBtn.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          nudgeCarousel(1);
        },
        { capture: true, signal: navAbort.signal }
      );
    }

    component.addEventListener('mouseenter', pause);
    component.addEventListener('mouseleave', resume);
    component.addEventListener('focusin', pause);
    component.addEventListener('focusout', function (e) {
      if (!component.contains(e.relatedTarget)) resume();
    });

    component.addEventListener(
      'touchstart',
      function () {
        pause();
        clearTimeout(touchPauseTimer);
        touchPauseTimer = setTimeout(function () {
          resume();
        }, 2500);
      },
      { passive: true }
    );

    slider.addEventListener(
      'wheel',
      function () {
        pause();
        clearTimeout(touchPauseTimer);
        touchPauseTimer = setTimeout(function () {
          resume();
        }, 2000);
      },
      { passive: true }
    );

    const ioTarget = slider;
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          inViewport = entry.isIntersecting;
          if (shouldRun()) startRaf();
          else stopRaf();
        });
      },
      { root: null, threshold: 0.01, rootMargin: '0px 0px 80px 0px' }
    );
    io.observe(ioTarget);

    ro = new ResizeObserver(function () {
      if (shouldRun() && rafId === null) startRaf();
    });
    ro.observe(slider);

    function onSectionUnload(e) {
      if (sectionEl && e.target === sectionEl) cleanup();
    }

    function cleanup() {
      stopRaf();
      clearTimeout(touchPauseTimer);
      clearTimeout(arrowResumeTimer);
      navAbort.abort();
      if (io) {
        io.disconnect();
        io = null;
      }
      if (ro) {
        ro.disconnect();
        ro = null;
      }
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      delete component.dataset.autoplayInitialized;
    }

    document.addEventListener('shopify:section:unload', onSectionUnload);

    document.addEventListener('visibilitychange', function () {
      pageVisible = document.visibilityState !== 'hidden';
      if (shouldRun()) startRaf();
      else stopRaf();
    });

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (shouldRun()) startRaf();
      });
    });
  }

  function initStepAutoplay(component, slider, nextBtn, sectionEl) {
    let timerId = null;
    let pauseCount = 0;
    let inViewport = true;
    let pageVisible = document.visibilityState !== 'hidden';
    let io = null;

    function clearTimer() {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    }

    function shouldRun() {
      return inViewport && pageVisible && pauseCount === 0;
    }

    function startTimer() {
      clearTimer();
      if (!shouldRun()) return;
      const ms = parseIntervalSeconds(component);
      timerId = setInterval(tick, ms);
    }

    function tick() {
      if (!shouldRun()) return;
      if (nextBtn.disabled || nextBtn.hasAttribute('disabled')) {
        slider.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      nextBtn.click();
    }

    function pause() {
      pauseCount++;
      clearTimer();
    }

    function resume() {
      pauseCount = Math.max(0, pauseCount - 1);
      if (shouldRun()) startTimer();
    }

    component.addEventListener('mouseenter', pause);
    component.addEventListener('mouseleave', resume);
    component.addEventListener('focusin', pause);
    component.addEventListener('focusout', function (e) {
      if (!component.contains(e.relatedTarget)) resume();
    });

    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          inViewport = entry.isIntersecting;
          if (shouldRun()) startTimer();
          else clearTimer();
        });
      },
      { root: null, threshold: 0, rootMargin: '0px 0px 50px 0px' }
    );
    io.observe(slider);

    function onSectionUnload(e) {
      if (sectionEl && e.target === sectionEl) cleanup();
    }

    function cleanup() {
      clearTimer();
      if (io) {
        io.disconnect();
        io = null;
      }
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      delete component.dataset.autoplayInitialized;
    }

    document.addEventListener('shopify:section:unload', onSectionUnload);

    document.addEventListener('visibilitychange', function () {
      pageVisible = document.visibilityState !== 'hidden';
      if (shouldRun()) startTimer();
      else clearTimer();
    });

    startTimer();
  }

  function initCarouselAutoplay(component) {
    if (component.dataset.autoplayInitialized === 'true') return;
    if (prefersReducedMotion()) return;

    const slider = component.querySelector('.slider');
    if (!slider) return;

    const continuous = component.getAttribute('data-carousel-autoplay-continuous') === 'true';
    const sectionEl = component.closest('.shopify-section');

    component.dataset.autoplayInitialized = 'true';

    if (continuous) {
      initContinuousAutoplay(component, slider, sectionEl);
    } else {
      const nextBtn = component.querySelector('.slider-button--next');
      if (!nextBtn) {
        delete component.dataset.autoplayInitialized;
        return;
      }
      initStepAutoplay(component, slider, nextBtn, sectionEl);
    }
  }

  function runInit() {
    document.querySelectorAll('slider-component[data-carousel-autoplay="true"]').forEach(initCarouselAutoplay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  } else {
    runInit();
  }

  document.addEventListener('shopify:section:load', function () {
    runInit();
  });
})();
